/**
 * Status categories for FSRS card due dates based on when they need to be reviewed
 */
export type FSRSDueStatus = "Now" | "Within24hr" | "Tomorrow" | "Within1wk" | "Future";

/**
 * Determines the due status category for an FSRS card based on its due timestamp
 *
 * @param dueTimestamp Unix timestamp in milliseconds when the card is due
 * @param now Current time in milliseconds (defaults to Date.now())
 * @returns Status category: "Now" (due now or overdue), "Within24hr" (due within 24 hours but not tomorrow),
 *          "Tomorrow" (due specifically tomorrow), "Within1wk" (due within 7 days but after tomorrow),
 *          or "Future" (due later than 7 days)
 */
export function getFSRSDueStatus(dueTimestamp: number, now: number = Date.now()): FSRSDueStatus {
  // Handle edge cases for invalid timestamps
  if (!Number.isFinite(dueTimestamp) || dueTimestamp < 0) {
    return "Now"; // Default to "Now" for invalid timestamps
  }

  const oneDayMs = 1000 * 60 * 60 * 24;
  const nowCutoff = now;
  const within24hrCutoff = now + oneDayMs;

  // Calculate tomorrow's start and end (midnight to midnight)
  const tomorrowStart = new Date(now);
  tomorrowStart.setHours(0, 0, 0, 0);
  tomorrowStart.setDate(tomorrowStart.getDate() + 1);
  const tomorrowEnd = new Date(tomorrowStart);
  tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);

  const week1Cutoff = now + oneDayMs * 7;

  if (dueTimestamp <= nowCutoff) {
    return "Now";
  }
  if (dueTimestamp <= within24hrCutoff) {
    // Check if it's specifically tomorrow
    if (dueTimestamp >= tomorrowStart.getTime() && dueTimestamp < tomorrowEnd.getTime()) {
      return "Tomorrow";
    }
    return "Within24hr";
  }
  if (dueTimestamp <= week1Cutoff) {
    return "Within1wk";
  }
  return "Future";
}
